/**
 * Utility functions for generating WAV files from accelerometer data in the browser
 */

/**
 * Extract array data from nested structure (handles uint8Data, uint16Data, etc.)
 * Properly converts signed integers (int8Data, int16Data) from unsigned to signed
 */
function extractArrayData(data) {
  if (!data) {
    return [];
  }

  // Direct array
  if (Array.isArray(data)) {
    return data;
  }

  // Nested structure with uint8Data, uint16Data, etc.
  if (data.uint8Data && Array.isArray(data.uint8Data)) {
    return data.uint8Data;
  }
  if (data.uint16Data && Array.isArray(data.uint16Data)) {
    return data.uint16Data;
  }
  if (data.uint32Data && Array.isArray(data.uint32Data)) {
    return data.uint32Data;
  }
  if (data.int8Data && Array.isArray(data.int8Data)) {
    return data.int8Data;
  }
  if (data.int16Data && Array.isArray(data.int16Data)) {
    return data.int16Data;
  }

  return [];
}

/**
 * Extract accelerometer raw data from a single accelerometer block
 */
export function extractAccelerometerBlockData(block) {
  const accelerometerData = {
    xRaw: [],
    yRaw: [],
    zRaw: [],
    sampleRate: null,
    startTime: null,
    endTime: null,
  };

  if (!block) {
    return accelerometerData;
  }

  // Extract sample rate from freq field
  if (block.freq !== undefined && block.freq !== null) {
    accelerometerData.sampleRate = block.freq;
  }

  // Extract precision from precision field
  if (block.precision !== undefined && block.precision !== null) {
    accelerometerData.precision = block.precision;
  }

  // Extract startTime and endTime
  if (block.startTime !== undefined && block.startTime !== null) {
    accelerometerData.startTime = block.startTime;
  }
  if (block.endTime !== undefined && block.endTime !== null) {
    accelerometerData.endTime = block.endTime;
  }

  // Extract xRaw, yRaw, zRaw arrays
  // Handle case where int8Data might be a JSON string
  if (block.xRaw) {
    if (typeof block.xRaw.int8Data === 'string') {
      try {
        block.xRaw.int8Data = JSON.parse(block.xRaw.int8Data);
      } catch (e) {
        // If parsing fails, treat as empty
        block.xRaw.int8Data = [];
      }
    }
    accelerometerData.xRaw = extractArrayData(block.xRaw);
  }

  if (block.yRaw) {
    if (typeof block.yRaw.int8Data === 'string') {
      try {
        block.yRaw.int8Data = JSON.parse(block.yRaw.int8Data);
      } catch (e) {
        block.yRaw.int8Data = [];
      }
    }
    accelerometerData.yRaw = extractArrayData(block.yRaw);
  }

  if (block.zRaw) {
    if (typeof block.zRaw.int8Data === 'string') {
      try {
        block.zRaw.int8Data = JSON.parse(block.zRaw.int8Data);
      } catch (e) {
        block.zRaw.int8Data = [];
      }
    }
    accelerometerData.zRaw = extractArrayData(block.zRaw);
  }

  return accelerometerData;
}

/**
 * Extract accelerometer raw data from TLV JSON
 */
export function extractAccelerometerData(tlvJson) {
  const accelerometerData = {
    xRaw: [],
    yRaw: [],
    zRaw: [],
    sampleRate: null,
  };

  let raw = null;

  // First, try to find accelerometerRaw in signalDataBlocks
  if (tlvJson.signalDataBlocks && Array.isArray(tlvJson.signalDataBlocks)) {
    for (const block of tlvJson.signalDataBlocks) {
      if (block.accelerometerRaw) {
        raw = block.accelerometerRaw;
        break;
      }
    }
  }

  // If not found, try root level
  if (!raw && tlvJson.accelerometerRaw) {
    raw = tlvJson.accelerometerRaw;
  }

  if (!raw) {
    return accelerometerData;
  }

  // Extract sample rate from freq field
  if (raw.freq !== undefined && raw.freq !== null) {
    accelerometerData.sampleRate = raw.freq;
  }

  // Extract xRaw, yRaw, zRaw arrays
  if (raw.xRaw) {
    accelerometerData.xRaw = extractArrayData(raw.xRaw);
  }

  if (raw.yRaw) {
    accelerometerData.yRaw = extractArrayData(raw.yRaw);
  }

  if (raw.zRaw) {
    accelerometerData.zRaw = extractArrayData(raw.zRaw);
  }

  return accelerometerData;
}

/**
 * Normalize array values to -1.0 to 1.0 range for audio
 */
function normalizeToAudio(array, bitsPerSample) {
  if (!array || array.length === 0) {
    return [];
  }

  const min = -(2 ** (bitsPerSample - 1));
  const max = 2 ** (bitsPerSample - 1);
  const range = max - min;

  if (range === 0) {
    // All values are the same, center at 0
    return array.map(() => 0);
  }

  // Normalize to -1.0 to 1.0
  return array.map((val) => ((val - min) / range) * 2 - 1);
}

/**
 * Generate WAV file blob from accelerometer data
 * @param {number[]} xData - X axis data
 * @param {number[]} yData - Y axis data
 * @param {number[]} zData - Z axis data
 * @param {number} sampleRate - Sample rate in Hz (default: 44100)
 * @returns {Blob} WAV file blob
 */
export function generateWavBlob(xData, yData, zData, sampleRate = 44100, bitsPerInputSample = 16) {
  // Normalize data to audio range
  const xNormalized = normalizeToAudio(xData, bitsPerInputSample);
  const yNormalized = normalizeToAudio(yData, bitsPerInputSample);
  const zNormalized = normalizeToAudio(zData, bitsPerInputSample);

  // Find the maximum length
  const maxLength = Math.max(xNormalized.length, yNormalized.length, zNormalized.length);

  // Pad shorter arrays with zeros
  while (xNormalized.length < maxLength) xNormalized.push(0);
  while (yNormalized.length < maxLength) yNormalized.push(0);
  while (zNormalized.length < maxLength) zNormalized.push(0);

  // Convert to 16-bit PCM samples
  const samples = [];
  for (let i = 0; i < maxLength; i += 1) {
    // Interleave: X, Y, Z, X, Y, Z, ...
    samples.push(Math.max(-32768, Math.min(32767, Math.round(xNormalized[i] * 32767))));
    samples.push(Math.max(-32768, Math.min(32767, Math.round(yNormalized[i] * 32767))));
    samples.push(Math.max(-32768, Math.min(32767, Math.round(zNormalized[i] * 32767))));
  }

  // WAV file format
  const numChannels = 3;
  const bitsPerSample = 16;
  const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
  const blockAlign = numChannels * (bitsPerSample / 8);
  const dataSize = samples.length * 2; // 2 bytes per sample
  const fileSize = 36 + dataSize; // Header size + data size

  // Create ArrayBuffer for WAV file
  const buffer = new ArrayBuffer(fileSize + 8); // +8 for "RIFF" and file size
  const view = new DataView(buffer);

  let offset = 0;

  // RIFF header
  offset = writeString(view, offset, 'RIFF');
  view.setUint32(offset, fileSize, true); // little-endian
  offset += 4;
  offset = writeString(view, offset, 'WAVE');

  // fmt chunk
  offset = writeString(view, offset, 'fmt ');
  view.setUint32(offset, 16, true); // fmt chunk size
  offset += 4;
  view.setUint16(offset, 1, true); // audio format (1 = PCM)
  offset += 2;
  view.setUint16(offset, numChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, byteRate, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, bitsPerSample, true);
  offset += 2;

  // data chunk
  offset = writeString(view, offset, 'data');
  view.setUint32(offset, dataSize, true);
  offset += 4;

  // Write interleaved samples (little-endian)
  for (let i = 0; i < samples.length; i += 1) {
    view.setInt16(offset, samples[i], true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Helper function to write string to DataView
 */
function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
  return offset + string.length;
}

/**
 * Download a blob as a file
 * @param {Blob} blob - Blob to download
 * @param {string} filename - Filename for download
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
